import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { decksAPI } from '../services/api';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import { ArrowLeft, RotateCcw, Volume2, Layers, Grid2x2, Keyboard, Check, X } from 'lucide-react';

const LANG_VOICES = { en: 'en-US', fi: 'fi-FI', fa: 'fa-IR' };

const QUALITY = [
  { value: 0, label: "Didn't know", color: '#ff6b8a' },
  { value: 2, label: 'Hard',        color: '#fb923c' },
  { value: 3, label: 'Almost',      color: '#fcd34d' },
  { value: 4, label: 'Good',        color: '#4ade80' },
  { value: 5, label: 'Easy',        color: '#60a5fa' },
];

const MODES = [
  { id: 'flashcard', label: 'Flashcard',       icon: <Layers size={20} />,   desc: 'Flip cards & rate yourself' },
  { id: 'choice',   label: 'Multiple Choice',  icon: <Grid2x2 size={20} />,  desc: 'Pick the correct answer' },
  { id: 'type',     label: 'Type It',          icon: <Keyboard size={20} />, desc: 'Write the translation' },
];

// Levenshtein distance for typo tolerance
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => Array.from({ length: n + 1 }, (_, j) => i === 0 ? j : j === 0 ? i : 0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
  return dp[m][n];
}

function checkAnswer(userInput, correct) {
  const u = userInput.trim().toLowerCase();
  const c = correct.trim().toLowerCase();
  if (u === c) return 'exact';
  if (levenshtein(u, c) <= 2) return 'close';
  // also check if any comma-separated variant matches
  const variants = c.split(/[,;/]/).map(v => v.trim());
  if (variants.some(v => u === v || levenshtein(u, v) <= 1)) return 'close';
  return 'wrong';
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildChoices(cards, current) {
  const correct = cards[current];
  const others = cards.filter((_, i) => i !== current);
  const distractors = shuffle(others).slice(0, 3);
  return shuffle([correct, ...distractors]);
}

export default function Practice() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deckTitle, setDeckTitle] = useState('');
  const [mode, setMode] = useState(null); // null = mode picker

  // Shared state
  const [current, setCurrent] = useState(0);
  const [done, setDone] = useState(false);
  const [results, setResults] = useState([]);
  const [reviewing, setReviewing] = useState(false);

  // Flashcard mode
  const [flipped, setFlipped] = useState(false);

  // Multiple choice
  const [choices, setChoices] = useState([]);
  const [chosen, setChosen] = useState(null); // index chosen
  const [choiceResult, setChoiceResult] = useState(null); // 'correct'|'wrong'

  // Type mode
  const [typed, setTyped] = useState('');
  const [typeResult, setTypeResult] = useState(null); // 'exact'|'close'|'wrong'|null
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [cardsRes, decksRes] = await Promise.all([
          decksAPI.getFlashcards(id),
          decksAPI.list(),
        ]);
        setCards(shuffle(cardsRes.data));
        const deck = decksRes.data.find(d => String(d.id) === String(id));
        if (deck) setDeckTitle(deck.title);
      } catch { toast.error('Failed to load flashcards'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  // Build choices when mode/current changes
  useEffect(() => {
    if (mode === 'choice' && cards.length > 0) {
      setChoices(buildChoices(cards, current));
      setChosen(null);
      setChoiceResult(null);
    }
  }, [mode, current, cards]);

  // Focus input in type mode
  useEffect(() => {
    if (mode === 'type' && inputRef.current) inputRef.current.focus();
  }, [mode, current]);

  const speak = (word, lang) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(word);
    u.lang = LANG_VOICES[lang] || 'en-US';
    window.speechSynthesis.speak(u);
  };

  const advance = useCallback(async (card, quality) => {
    setReviewing(true);
    try {
      const r = await decksAPI.review(card.id, quality);
      setResults(prev => [...prev, { card, quality, next_review_in_days: r.data.next_review_in_days }]);
    } catch { toast.error('Failed to record result'); }
    finally { setReviewing(false); }

    setTimeout(() => {
      if (current + 1 >= cards.length) {
        setDone(true);
      } else {
        setCurrent(c => c + 1);
        setFlipped(false);
        setTyped('');
        setTypeResult(null);
        setChosen(null);
        setChoiceResult(null);
      }
    }, mode === 'flashcard' ? 0 : 900);
  }, [current, cards.length, mode]);

  const restart = (newMode) => {
    setCards(prev => shuffle([...prev]));
    setCurrent(0); setDone(false); setResults([]);
    setFlipped(false); setTyped(''); setTypeResult(null);
    setChosen(null); setChoiceResult(null);
    setMode(newMode || mode);
  };

  // ── LOADING ──
  if (loading) return (
    <Layout>
      <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>Loading…</div>
    </Layout>
  );

  if (cards.length === 0) return (
    <Layout>
      <button className="btn btn-ghost" onClick={() => navigate('/decks')} style={{ marginBottom: '1.5rem' }}>
        <ArrowLeft size={14} /> Back to Decks
      </button>
      <div className="empty-state">
        <h3>This deck has no cards</h3>
        <p>Add words via the Translate page or manually in My Decks</p>
      </div>
    </Layout>
  );

  // ── MODE PICKER ──
  if (!mode) return (
    <Layout>
      <button className="btn btn-ghost" onClick={() => navigate('/decks')} style={{ marginBottom: '1.5rem' }}>
        <ArrowLeft size={14} /> {deckTitle || 'Back'}
      </button>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.3rem' }}>{deckTitle}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {cards.length} card{cards.length !== 1 ? 's' : ''} · Choose a study mode
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          {MODES.map(m => (
            cards.length < 4 && m.id === 'choice' ? (
              <div key={m.id} className="card" style={{ opacity: 0.4, cursor: 'not-allowed' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ color: 'var(--text-faint)', flexShrink: 0 }}>{m.icon}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{m.label}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: '0.1rem' }}>
                      Need at least 4 cards
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <button key={m.id} className="card" onClick={() => setMode(m.id)}
                style={{ cursor: 'pointer', textAlign: 'left', transition: 'all 0.18s', border: '1px solid var(--border)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-soft)', flexShrink: 0 }}>
                    {m.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{m.label}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>{m.desc}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', color: 'var(--text-faint)', fontSize: '1.2rem' }}>›</div>
                </div>
              </button>
            )
          ))}
        </div>
      </div>
    </Layout>
  );

  // ── DONE SCREEN ──
  if (done) {
    const correct = results.filter(r => r.quality >= 4).length;
    const pct = Math.round((correct / results.length) * 100);
    return (
      <Layout>
        <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center', paddingTop: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.8rem' }}>
            {pct >= 80 ? '🎉' : pct >= 50 ? '💪' : '📚'}
          </div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.3rem' }}>Session complete!</h2>
          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', margin: '1rem 0 1.5rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--green)' }}>{correct}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>CORRECT</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--red)' }}>{results.length - correct}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>MISSED</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--accent-soft)' }}>{pct}%</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>SCORE</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', marginBottom: '1.5rem', textAlign: 'left', maxHeight: 280, overflowY: 'auto' }}>
            {results.map((r, i) => (
              <div key={i} className="card card-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: r.quality >= 4 ? 'rgba(61,214,140,0.15)' : 'rgba(255,92,124,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {r.quality >= 4
                    ? <Check size={11} color="var(--green)" />
                    : <X size={11} color="var(--red)" />}
                </div>
                <span style={{ fontWeight: 600, fontSize: '0.88rem', flex: 1 }}>{r.card.word}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.card.translation}</span>
                <span style={{ fontSize: '0.68rem', color: 'var(--text-faint)' }}>+{r.next_review_in_days}d</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" style={{ width: 'auto' }} onClick={() => restart()}>
              <RotateCcw size={13} /> Try Again
            </button>
            <button className="btn btn-secondary" style={{ width: 'auto' }} onClick={() => { setMode(null); restart(null); }}>
              Change Mode
            </button>
            <button className="btn btn-primary" style={{ width: 'auto' }} onClick={() => navigate('/decks')}>
              Back to Decks
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const card = cards[current];
  const progress = (current / cards.length) * 100;

  return (
    <Layout>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <button className="btn btn-ghost" onClick={() => setMode(null)}>
          <ArrowLeft size={14} /> {deckTitle || 'Back'}
        </button>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, background: 'var(--bg-raised)', padding: '0.3rem 0.7rem', borderRadius: 6, border: '1px solid var(--border)' }}>
          {MODES.find(m => m.id === mode)?.label}
        </span>
      </div>

      {/* Progress */}
      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600 }}>
          <span>{current + 1} / {cards.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div style={{ height: 3, background: 'var(--bg-raised)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, var(--accent), var(--blue))', borderRadius: 99, transition: 'width 0.4s ease' }} />
        </div>
      </div>

      {/* ══ FLASHCARD MODE ══ */}
      {mode === 'flashcard' && (
        <>
          <div onClick={() => setFlipped(f => !f)}
            style={{ cursor: 'pointer', perspective: '1000px', maxWidth: 520, margin: '0 auto 1.5rem' }}>
            <div style={{
              position: 'relative', minHeight: 210,
              transformStyle: 'preserve-3d',
              transform: flipped ? 'rotateY(180deg)' : 'rotateY(0)',
              transition: 'transform 0.4s ease',
            }}>
              {/* Front */}
              <div className="card" style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem', borderColor: 'var(--border-glow)' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1rem' }}>Tap to reveal</div>
                <div style={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: '0.8rem' }}>{card.word}</div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {card.cefr_level && <span className={`badge badge-${card.cefr_level.toLowerCase()}`}>{card.cefr_level}</span>}
                  <button className="btn btn-icon" style={{ width: 28, height: 28 }}
                    onClick={e => { e.stopPropagation(); speak(card.word, card.source_lang || 'fi'); }}>
                    <Volume2 size={12} />
                  </button>
                </div>
              </div>
              {/* Back */}
              <div className="card" style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem', background: 'var(--bg-raised)', borderColor: 'var(--accent)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-soft)', marginBottom: '0.6rem' }}>{card.translation || '—'}</div>
                <button className="btn btn-icon" style={{ width: 28, height: 28, marginBottom: '0.6rem' }}
                  onClick={() => speak(card.translation, card.target_lang || 'en')}>
                  <Volume2 size={12} />
                </button>
                {card.example_sentence && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.7, maxWidth: 340 }}>
                    "{card.example_sentence}"
                  </p>
                )}
              </div>
            </div>
          </div>

          {flipped ? (
            <div style={{ maxWidth: 520, margin: '0 auto' }}>
              <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.6rem', fontWeight: 600 }}>How well did you know it?</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.4rem' }}>
                {QUALITY.map(q => (
                  <button key={q.value} disabled={reviewing} onClick={() => advance(card, q.value)}
                    style={{ background: `${q.color}15`, border: `1.5px solid ${q.color}35`, borderRadius: 9, color: q.color, padding: '0.55rem 0.2rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '0.73rem', transition: 'all 0.15s', opacity: reviewing ? 0.5 : 1 }}
                    onMouseEnter={e => e.currentTarget.style.background = `${q.color}28`}
                    onMouseLeave={e => e.currentTarget.style.background = `${q.color}15`}
                  >{q.label}</button>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: 'var(--text-faint)', fontSize: '0.78rem' }}>Click the card to see the answer</p>
          )}
        </>
      )}

      {/* ══ MULTIPLE CHOICE MODE ══ */}
      {mode === 'choice' && (
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          {/* Question card */}
          <div className="card" style={{ textAlign: 'center', marginBottom: '1.25rem', borderColor: 'var(--border-glow)', padding: '1.75rem 2rem' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
              What does this mean?
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: '0.6rem' }}>{card.word}</div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', alignItems: 'center' }}>
              {card.cefr_level && <span className={`badge badge-${card.cefr_level.toLowerCase()}`}>{card.cefr_level}</span>}
              <button className="btn btn-icon" style={{ width: 26, height: 26 }}
                onClick={() => speak(card.word, card.source_lang || 'fi')}>
                <Volume2 size={11} />
              </button>
            </div>
          </div>

          {/* Choices */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
            {choices.map((c, i) => {
              const isCorrect = c.id === card.id;
              const isChosen = chosen === i;
              let bg = 'var(--bg-card)';
              let border = 'var(--border)';
              let color = 'var(--text-primary)';
              if (choiceResult) {
                if (isCorrect) { bg = 'rgba(61,214,140,0.12)'; border = 'var(--green)'; color = 'var(--green)'; }
                else if (isChosen && !isCorrect) { bg = 'rgba(255,92,124,0.12)'; border = 'var(--red)'; color = 'var(--red)'; }
                else { bg = 'var(--bg-card)'; border = 'var(--border)'; color = 'var(--text-faint)'; }
              }
              return (
                <button key={i} disabled={!!choiceResult}
                  onClick={async () => {
                    if (choiceResult) return;
                    setChosen(i);
                    const correct = isCorrect;
                    setChoiceResult(correct ? 'correct' : 'wrong');
                    await advance(card, correct ? 5 : 0);
                  }}
                  style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 10, padding: '0.85rem 1.1rem', cursor: choiceResult ? 'default' : 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: '0.88rem', color, transition: 'all 0.18s', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.7rem' }}
                  onMouseEnter={e => { if (!choiceResult) e.currentTarget.style.borderColor = 'var(--accent-soft)'; }}
                  onMouseLeave={e => { if (!choiceResult) e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: choiceResult ? 'transparent' : 'var(--bg-raised)', border: `1px solid ${choiceResult ? 'transparent' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 800, flexShrink: 0, color: choiceResult && isCorrect ? 'var(--green)' : choiceResult && isChosen ? 'var(--red)' : 'var(--text-faint)' }}>
                    {choiceResult && isCorrect ? <Check size={12} /> : choiceResult && isChosen ? <X size={12} /> : String.fromCharCode(65 + i)}
                  </span>
                  {c.translation || '—'}
                </button>
              );
            })}
          </div>

          {choiceResult && (
            <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.82rem', color: choiceResult === 'correct' ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
              {choiceResult === 'correct' ? '✓ Correct!' : `✗ The answer was: ${card.translation}`}
            </div>
          )}
        </div>
      )}

      {/* ══ TYPE IT MODE ══ */}
      {mode === 'type' && (
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          {/* Question */}
          <div className="card" style={{ textAlign: 'center', marginBottom: '1.25rem', borderColor: 'var(--border-glow)', padding: '1.75rem 2rem' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
              Type the translation
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: '0.6rem' }}>{card.word}</div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', alignItems: 'center' }}>
              {card.cefr_level && <span className={`badge badge-${card.cefr_level.toLowerCase()}`}>{card.cefr_level}</span>}
              <button className="btn btn-icon" style={{ width: 26, height: 26 }}
                onClick={() => speak(card.word, card.source_lang || 'fi')}>
                <Volume2 size={11} />
              </button>
            </div>
            {card.example_sentence && (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: '0.8rem', fontStyle: 'italic' }}>
                Hint: "{card.example_sentence}"
              </p>
            )}
          </div>

          {/* Input */}
          <div style={{ position: 'relative', marginBottom: '0.8rem' }}>
            <input
              ref={inputRef}
              value={typed}
              onChange={e => !typeResult && setTyped(e.target.value)}
              onKeyDown={async e => {
                if (e.key === 'Enter' && typed.trim() && !typeResult) {
                  const result = checkAnswer(typed, card.translation || '');
                  setTypeResult(result);
                  const quality = result === 'exact' ? 5 : result === 'close' ? 3 : 0;
                  await advance(card, quality);
                }
              }}
              placeholder="Type translation and press Enter…"
              style={{
                width: '100%', background: typeResult
                  ? typeResult === 'wrong' ? 'rgba(255,92,124,0.08)' : 'rgba(61,214,140,0.08)'
                  : 'var(--bg-card)',
                border: `1.5px solid ${typeResult === 'wrong' ? 'var(--red)' : typeResult ? 'var(--green)' : 'var(--border)'}`,
                borderRadius: 10, padding: '0.85rem 1rem',
                color: 'var(--text-primary)', fontFamily: 'inherit',
                fontSize: '1rem', outline: 'none', transition: 'all 0.2s',
              }}
              disabled={!!typeResult}
            />
          </div>

          {/* Result feedback */}
          {typeResult && (
            <div style={{ borderRadius: 10, padding: '0.8rem 1rem', marginBottom: '0.8rem', background: typeResult === 'wrong' ? 'rgba(255,92,124,0.1)' : 'rgba(61,214,140,0.1)', border: `1px solid ${typeResult === 'wrong' ? 'rgba(255,92,124,0.3)' : 'rgba(61,214,140,0.3)'}` }}>
              {typeResult === 'exact' && <p style={{ color: 'var(--green)', fontWeight: 700, fontSize: '0.88rem' }}>✓ Perfect!</p>}
              {typeResult === 'close' && (
                <>
                  <p style={{ color: 'var(--yellow)', fontWeight: 700, fontSize: '0.88rem' }}>~ Almost! Small typo.</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.2rem' }}>Correct: <strong>{card.translation}</strong></p>
                </>
              )}
              {typeResult === 'wrong' && (
                <>
                  <p style={{ color: 'var(--red)', fontWeight: 700, fontSize: '0.88rem' }}>✗ Not quite.</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.2rem' }}>Answer: <strong style={{ color: 'var(--text-primary)' }}>{card.translation}</strong></p>
                </>
              )}
            </div>
          )}

          {!typeResult && (
            <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-faint)' }}>
              Press Enter to check · Small typos are OK
            </p>
          )}

          {/* Skip button */}
          {!typeResult && (
            <div style={{ textAlign: 'center', marginTop: '0.8rem' }}>
              <button className="btn btn-ghost" style={{ fontSize: '0.78rem' }}
                onClick={async () => {
                  setTypeResult('wrong');
                  await advance(card, 0);
                }}>
                Skip (show answer)
              </button>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}